/**
 * printPageFilter.ts
 * Multi-field search/filter for the Preview step print list.
 * Free-text scans label + group key + header values + sample row cells.
 * Structured filters AND across chosen columns.
 */

import type { DetailedGroup } from "@/utils/dataGrouper";
import { isRowMetaKey } from "@/types/excel";

/** One structured column filter (column contains value). */
export interface ColumnFilter {
  id: string;
  column: string;
  /** Case-insensitive substring; empty = ignore this filter. */
  value: string;
}

const SAMPLE_ROWS = 8;

/** Flatten searchable text for one chứng từ (group). */
export function buildGroupSearchText(
  group: DetailedGroup,
  extras: { label?: string; groupValue?: string } = {}
): string {
  const parts: string[] = [];
  if (extras.label) parts.push(extras.label);
  if (extras.groupValue) parts.push(extras.groupValue);
  parts.push(group.groupValue, group.groupKey);

  for (const [key, val] of Object.entries(group.headerValues ?? {})) {
    if (isRowMetaKey(key) || val == null) continue;
    const s = String(val).trim();
    if (s) parts.push(s, key);
  }

  // Sample detail rows so item-level fields (SKU, product name…) are findable.
  for (const row of group.rows.slice(0, SAMPLE_ROWS)) {
    for (const [key, val] of Object.entries(row)) {
      if (isRowMetaKey(key) || val == null) continue;
      const s = String(val).trim();
      if (s) parts.push(s);
    }
  }

  return parts.join(" \u0001 ").toLowerCase();
}

/**
 * Free-text match: space-separated tokens are AND'ed (all must appear somewhere
 * in the blob). Empty needle matches everything.
 */
export function matchesQuickSearch(searchText: string, needle: string): boolean {
  const q = needle.trim().toLowerCase();
  if (!q) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const hay = searchText.toLowerCase();
  return tokens.every((t) => hay.includes(t));
}

/** Value of a column for a group: headerValues first, else first non-empty row cell. */
export function groupColumnValue(group: DetailedGroup, column: string): string {
  if (!column) return "";
  const hv = group.headerValues?.[column];
  if (hv != null && String(hv).trim() !== "") return String(hv).trim();
  for (const row of group.rows) {
    const v = row[column];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

/**
 * Structured filters: every non-empty filter must match (AND).
 * Match is case-insensitive substring on the column value.
 */
export function matchesColumnFilters(
  group: DetailedGroup,
  filters: ColumnFilter[]
): boolean {
  for (const f of filters) {
    const col = f.column.trim();
    const val = f.value.trim().toLowerCase();
    if (!col || !val) continue;
    const cell = groupColumnValue(group, col).toLowerCase();
    if (!cell.includes(val)) return false;
  }
  return true;
}

export function activeColumnFilterCount(filters: ColumnFilter[]): number {
  return filters.filter((f) => f.column.trim() && f.value.trim()).length;
}

/** Columns useful for filtering: data headers minus empty meta keys. */
export function filterableColumns(headers: string[]): string[] {
  return headers.filter((h) => h && !isRowMetaKey(h));
}

/** De-accent + lower for column name heuristics. */
export function normalizeColumnKey(h: string): string {
  return h
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Prefer human-friendly filter columns: name > PO > address > code.
 * Used for default "Thêm cột" and list subtitles.
 */
export function scoreFilterColumn(h: string): number {
  const d = normalizeColumnKey(h);
  if (/ten|name|sieu thi/.test(d)) return 40;
  if (/po|don hang/.test(d)) return 30;
  if (/dia chi|address|tuyen|kho/.test(d)) return 20;
  if (/(^|\s)ma(\s|$)|ma ch|ma diem|code|sku/.test(d)) return 10;
  return 0;
}

export function preferredFilterColumn(columns: string[]): string {
  if (columns.length === 0) return "";
  return [...columns].sort((a, b) => scoreFilterColumn(b) - scoreFilterColumn(a))[0] ?? "";
}

/** Auto subtitle columns under list labels (exclude grouping cols). */
export function suggestExtraColumns(
  columns: string[],
  groupingColumns: string[],
  max = 2
): string[] {
  const groupSet = new Set(groupingColumns.map((c) => c.toLowerCase()));
  return columns
    .filter((h) => !groupSet.has(h.toLowerCase()))
    .map((h) => ({ h, score: scoreFilterColumn(h) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, max)
    .map((x) => x.h);
}
